import { supabase } from "../config/supabase";

/**
 * Service for managing CIPA mandates (períodos de gestão)
 * NR-05 compliance: mandates last 1-2 years with specific rules
 */

export type StatusMandato = "ativo" | "finalizado" | "suspenso";

export interface Mandato {
  id?: string;
  empresa_id: string;
  numero_mandato: number;
  data_inicio: string;
  data_fim: string;
  presidente_id?: string | null;
  secretario_id?: string | null;
  status: StatusMandato;
  eleicao_realizada: boolean;
  data_eleicao?: string | null;
  created_at?: string;
  updated_at?: string;
  criado_por?: string | null;
}

export interface MandatoComNomes extends Mandato {
  presidente_nome?: string;
  secretario_nome?: string;
}

/**
 * Service for CIPA mandate management
 */
export const mandatoService = {
  /**
   * Cria um novo mandato da CIPA.
   * Valida duração (1-2 anos) e evita sobreposição de períodos.
   * @param mandato - Dados do mandato (sem id/created_at/updated_at).
   * @returns Mandato criado.
   * @throws Erro de validação ou falha na persistência.
   */
  async criar(
    mandato: Omit<Mandato, "id" | "created_at" | "updated_at">,
  ): Promise<Mandato> {
    try {
      // Validate dates
      const dataInicio = new Date(mandato.data_inicio);
      const dataFim = new Date(mandato.data_fim);

      if (dataFim <= dataInicio) {
        throw new Error("Data de fim deve ser posterior à data de início");
      }

      // Calculate duration in days
      const diffTime = Math.abs(dataFim.getTime() - dataInicio.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const diffYears = diffDays / 365;

      // NR-05: mandate should be 1-2 years (with some tolerance)
      if (diffYears < 0.9 || diffYears > 2.1) {
        throw new Error("Duração do mandato deve ser entre 1 e 2 anos (NR-05)");
      }

      // Check for overlapping mandates
      const { data: overlapping, error: checkError } = await supabase
        .from("cp_cipa_mandato")
        .select("id, numero_mandato, data_inicio, data_fim")
        .eq("empresa_id", mandato.empresa_id)
        .or(
          `and(data_inicio.lte.${mandato.data_fim},data_fim.gte.${mandato.data_inicio})`,
        );

      if (checkError) throw checkError;

      if (overlapping && overlapping.length > 0) {
        throw new Error(
          `Já existe um mandato ativo no período especificado (Mandato #${overlapping[0].numero_mandato})`,
        );
      }

      const { data, error } = await supabase
        .from("cp_cipa_mandato")
        .insert([mandato])
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao criar mandato:", error);
        throw error;
      }

      console.log("✅ Mandato criado com sucesso:", data);
      return data as Mandato;
    } catch (error) {
      console.error("❌ Erro ao criar mandato:", error);
      throw error;
    }
  },

  /**
   * Atualiza um mandato existente.
   * Se alterar datas, revalida consistência do período.
   * @param id - ID do mandato.
   * @param updates - Campos a atualizar.
   * @returns Mandato atualizado.
   * @throws Erro de validação ou falha na persistência.
   */
  async atualizar(id: string, updates: Partial<Mandato>): Promise<Mandato> {
    try {
      // If updating dates, validate
      if (updates.data_inicio || updates.data_fim) {
        const { data: current } = await supabase
          .from("cp_cipa_mandato")
          .select("data_inicio, data_fim")
          .eq("id", id)
          .single();

        const dataInicio = new Date(
          updates.data_inicio || current?.data_inicio,
        );
        const dataFim = new Date(updates.data_fim || current?.data_fim);

        if (dataFim <= dataInicio) {
          throw new Error("Data de fim deve ser posterior à data de início");
        }
      }

      const { data, error } = await supabase
        .from("cp_cipa_mandato")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao atualizar mandato:", error);
        throw error;
      }

      console.log("✅ Mandato atualizado com sucesso:", data);
      return data as Mandato;
    } catch (error) {
      console.error("❌ Erro ao atualizar mandato:", error);
      throw error;
    }
  },

  /**
   * Obtém um mandato pelo ID, incluindo nomes de presidente/secretário.
   * @param id - ID do mandato.
   * @returns Mandato com nomes ou null.
   */
  async obter(id: string): Promise<MandatoComNomes | null> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_mandato")
        .select(
          `
          *,
          presidente:cp_usuarios!presidente_id(nome_completo),
          secretario:cp_usuarios!secretario_id(nome_completo)
        `,
        )
        .eq("id", id)
        .single();

      if (error) throw error;

      return {
        ...data,
        presidente_nome: data.presidente?.nome_completo,
        secretario_nome: data.secretario?.nome_completo,
      } as MandatoComNomes;
    } catch (error) {
      console.error("❌ Erro ao obter mandato:", error);
      return null;
    }
  },

  /**
   * Lista mandatos de uma empresa, ordenados por data de início.
   * @param empresaId - ID da empresa.
   * @returns Lista de mandatos com nomes associados.
   */
  async listar(empresaId: string): Promise<MandatoComNomes[]> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_mandato")
        .select(
          `
          *,
          presidente:cp_usuarios!presidente_id(nome_completo),
          secretario:cp_usuarios!secretario_id(nome_completo)
        `,
        )
        .eq("empresa_id", empresaId)
        .order("data_inicio", { ascending: false });

      if (error) {
        console.error("❌ Erro ao listar mandatos:", error);
        throw error;
      }

      return (data || []).map((m) => ({
        ...m,
        presidente_nome: m.presidente?.nome_completo,
        secretario_nome: m.secretario?.nome_completo,
      })) as MandatoComNomes[];
    } catch (error) {
      console.error("❌ Erro ao listar mandatos:", error);
      throw error;
    }
  },

  /**
   * Obtém o mandato ativo de uma empresa.
   * @param empresaId - ID da empresa.
   * @returns Mandato ativo ou null se inexistente.
   */
  async obterAtivo(empresaId: string): Promise<MandatoComNomes | null> {
    try {
      // Query simplificada sem joins problemáticos
      const { data, error } = await supabase
        .from("cp_cipa_mandato")
        .select("*")
        .eq("empresa_id", empresaId)
        .eq("status", "ativo")
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null; // No rows returned
        }
        throw error;
      }

      // Retornar sem os nomes (serão buscados separadamente se necessário)
      return {
        ...data,
        presidente_nome: undefined,
        secretario_nome: undefined,
      } as MandatoComNomes;
    } catch (error) {
      console.error("❌ Erro ao obter mandato ativo:", error);
      return null;
    }
  },

  /**
   * Finaliza um mandato (altera status para "finalizado").
   * @param id - ID do mandato.
   * @returns Mandato atualizado.
   */
  async finalizar(id: string): Promise<Mandato> {
    try {
      const updates: Partial<Mandato> = {
        status: "finalizado",
        data_fim: new Date().toISOString(),
      };

      return await this.atualizar(id, updates);
    } catch (error) {
      console.error("❌ Erro ao finalizar mandato:", error);
      throw error;
    }
  },

  /**
   * Suspende um mandato.
   * @param id - ID do mandato.
   * @param motivo - Motivo da suspensão (registro futuro).
   * @returns Mandato atualizado.
   */
  async suspender(id: string, motivo: string): Promise<Mandato> {
    try {
      return await this.atualizar(id, {
        status: "suspenso",
      });
    } catch (error) {
      console.error("❌ Erro ao suspender mandato:", error);
      throw error;
    }
  },

  /**
   * Exclui um mandato (somente se não houver membros associados).
   * @param id - ID do mandato.
   * @throws Erro quando existem membros vinculados.
   */
  async excluir(id: string): Promise<void> {
    try {
      // Check if mandate has members
      const { count } = await supabase
        .from("cp_cipa_membros")
        .select("*", { count: "exact", head: true })
        .eq("mandato_id", id);

      if (count && count > 0) {
        throw new Error(
          "Não é possível excluir mandato com membros cadastrados",
        );
      }

      const { error } = await supabase
        .from("cp_cipa_mandato")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("❌ Erro ao excluir mandato:", error);
        throw error;
      }

      console.log("✅ Mandato excluído com sucesso");
    } catch (error) {
      console.error("❌ Erro ao excluir mandato:", error);
      throw error;
    }
  },

  /**
   * Obtém o próximo número sequencial de mandato para a empresa.
   * @param empresaId - ID da empresa.
   * @returns Próximo número de mandato.
   */
  async obterProximoNumero(empresaId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_mandato")
        .select("numero_mandato")
        .eq("empresa_id", empresaId)
        .order("numero_mandato", { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === "PGRST116") return 1; // No mandates yet
        throw error;
      }

      return (data?.numero_mandato || 0) + 1;
    } catch (error) {
      console.error("❌ Erro ao obter próximo número de mandato:", error);
      return 1;
    }
  },

  /**
   * Calcula a duração do mandato em meses aproximados.
   * @param dataInicio - Data de início.
   * @param dataFim - Data de fim.
   * @returns Duração em meses.
   */
  calcularDuracao(dataInicio: string, dataFim: string): number {
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    const diffTime = Math.abs(fim.getTime() - inicio.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.round(diffDays / 30);
  },

  /**
   * Verifica se o mandato expira em até 60 dias.
   * @param dataFim - Data de fim do mandato.
   * @returns true se expira em até 60 dias.
   */
  isExpirando(dataFim: string): boolean {
    const fim = new Date(dataFim);
    const hoje = new Date();
    const diffTime = fim.getTime() - hoje.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 60;
  },
};
